// @spec SUP-STR-016
'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ChevronLeft, ChevronRight, Download, FileSpreadsheet } from 'lucide-react';
import * as React from 'react';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import {
  ComplianceMatrixFilters,
  ComplianceMatrixTable,
  ComplianceOverviewSummaryCards,
  useCategories,
  useComplianceOverview,
  useExportComplianceOverview,
} from '@/features/suppliers';
import type { ComplianceOverviewFilters, ComplianceOverviewResponse } from '@/features/suppliers/types';
import { useOrganizations } from '@/hooks/use-organizations';
import i18n from '@/lib/i18n';
import { useTranslation } from 'react-i18next';

// URL search params schema for validation
const complianceMatrixSearchSchema = z.object({
  page: z.coerce.number().optional().default(1),
  pageSize: z.coerce.number().optional().default(50),
  search: z.string().optional(),
  categoryId: z.string().optional(),
  tier: z
    .union([
      z.array(z.enum(['critical', 'high', 'medium', 'low'])),
      z.enum(['critical', 'high', 'medium', 'low']),
    ])
    .optional()
    .transform((val) => (val ? (Array.isArray(val) ? val : [val]) : undefined)),
  complianceStatus: z.enum(['compliant', 'partial', 'non_compliant', 'unknown']).optional(),
});

export const Route = createFileRoute('/_authenticated/suppliers/compliance-matrix')({
  validateSearch: complianceMatrixSearchSchema,
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.complianceMatrix'),
  }),
  component: ComplianceMatrixPage,
});

/**
 * Compliance Matrix page - aggregated compliance view for all suppliers.
 */
function ComplianceMatrixPage() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const searchParams = Route.useSearch();
  const { organizations, isLoading: orgsLoading } = useOrganizations();

  // Get active organization
  const activeOrg = organizations.find((org) => org.isActive);

  // Fetch categories for filter dropdown
  const { data: categoriesData } = useCategories({
    enabled: Boolean(activeOrg),
  });

  // Extract filter state from URL search params
  const filters: ComplianceOverviewFilters = React.useMemo(
    () => ({
      categoryId: searchParams.categoryId ?? null,
      tier: searchParams.tier ?? null,
      complianceStatus: searchParams.complianceStatus ?? null,
      search: searchParams.search ?? null,
    }),
    [searchParams]
  );

  // Fetch compliance overview data
  const queryPage = searchParams.page;
  const queryPageSize = searchParams.pageSize;
  const {
    data: overviewData,
    isLoading: overviewLoading,
    isFetching: overviewFetching,
  } = useComplianceOverview({
    page: queryPage,
    pageSize: queryPageSize,
    ...filters,
    enabled: Boolean(activeOrg),
  });

  // Export mutation
  const exportMutation = useExportComplianceOverview();

  const effectiveOverviewData: ComplianceOverviewResponse = React.useMemo(
    () =>
      overviewData ?? {
        items: [],
        pagination: {
          page: queryPage,
          pageSize: queryPageSize,
          totalItems: 0,
          totalPages: 1,
        },
        summary: {
          totalSuppliers: 0,
          compliantCount: 0,
          partialCount: 0,
          nonCompliantCount: 0,
          unknownCount: 0,
        },
      },
    [overviewData, queryPage, queryPageSize]
  );

  // Update filters in URL
  const handleFiltersChange = React.useCallback(
    (newFilters: ComplianceOverviewFilters) => {
      void navigate({
        search: {
          ...searchParams,
          categoryId: newFilters.categoryId ?? undefined,
          tier: newFilters.tier ?? undefined,
          complianceStatus: newFilters.complianceStatus ?? undefined,
          search: newFilters.search ?? undefined,
          page: 1, // Reset to first page on filter change
        } as never,
        replace: true,
      });
    },
    [navigate, searchParams]
  );

  // Handle pagination
  const handlePageChange = React.useCallback(
    (page: number) => {
      void navigate({
        search: { ...searchParams, page } as never,
        replace: true,
      });
    },
    [navigate, searchParams]
  );

  // Handle supplier click - navigate to detail
  const handleSupplierClick = React.useCallback(
    (supplierId: string) => {
      void navigate({
        to: '/suppliers/$supplierId',
        params: { supplierId },
      });
    },
    [navigate]
  );

  // Handle export
  const handleExport = React.useCallback(
    (format: 'csv' | 'xlsx') => {
      exportMutation.mutate({ format, filters });
    },
    [exportMutation, filters]
  );

  // Show loading while checking organizations
  if (orgsLoading) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-7xl">
          <div className="animate-pulse space-y-4">
            <div className="h-8 w-64 rounded bg-muted" />
            <div className="h-12 rounded bg-muted" />
            <div className="h-64 rounded bg-muted" />
          </div>
        </div>
      </div>
    );
  }

  // No active organization selected
  if (!activeOrg) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-7xl">
          <div className="rounded-lg border border-dashed p-12 text-center">
            <h2 className="text-lg font-semibold text-foreground">{t('common:routeErrors.noOrgSelected')}</h2>
            <p className="mt-2 text-muted-foreground">
              {t('common:routeErrors.noOrgSelectedDesc')}
            </p>
            <Button
              variant="outline"
              onClick={() => void navigate({ to: '/settings/organizations' })}
              className="mt-4"
            >
              {t('common:routeErrors.goToOrganizations')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const pagination = effectiveOverviewData.pagination;
  const totalPages = pagination.totalPages;
  const currentPage = pagination.page;

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="mx-auto max-w-7xl space-y-6">
        {/* Header */}
        <header className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold text-foreground">Compliance Matrix</h1>
            <p className="mt-1 text-muted-foreground">
              Aggregated compliance status across NIS2, ESG, and certifications for all suppliers
            </p>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" disabled={exportMutation.isPending}>
                <Download className="mr-2 h-4 w-4" />
                {exportMutation.isPending ? 'Exporting...' : 'Export'}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => {
                  handleExport('csv');
                }}
              >
                <FileSpreadsheet className="mr-2 h-4 w-4" />
                Export as CSV
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  handleExport('xlsx');
                }}
              >
                <FileSpreadsheet className="mr-2 h-4 w-4" />
                Export as Excel
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        {/* Summary Cards */}
        <ComplianceOverviewSummaryCards
          summary={effectiveOverviewData.summary}
          isLoading={overviewLoading}
        />

        {/* Filter Bar */}
        <ComplianceMatrixFilters
          filters={filters}
          onFiltersChange={handleFiltersChange}
          categories={categoriesData?.items ?? []}
        />

        {/* Loading state */}
        {overviewLoading ? (
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-16 w-full rounded-lg" />
            ))}
          </div>
        ) : effectiveOverviewData.items.length === 0 ? (
          <div className="rounded-lg border border-dashed p-12 text-center">
            <h3 className="text-lg font-semibold text-foreground">{t('common:routeErrors.noSuppliersFound')}</h3>
            <p className="mt-2 text-muted-foreground">
              {(filters.categoryId ?? filters.tier ?? filters.complianceStatus ?? filters.search)
                ? 'Try adjusting your filters to see more results.'
                : 'Add suppliers to see their compliance status here.'}
            </p>
            {(filters.categoryId ?? filters.tier ?? filters.complianceStatus ?? filters.search) !==
              null && (
              <Button
                variant="outline"
                className="mt-4"
                onClick={() => {
                  handleFiltersChange({
                    categoryId: null,
                    tier: null,
                    complianceStatus: null,
                    search: null,
                  });
                }}
              >
                Clear Filters
              </Button>
            )}
          </div>
        ) : (
          <>
            {/* Compliance Matrix Table */}
            <ComplianceMatrixTable
              items={effectiveOverviewData.items}
              onRowClick={handleSupplierClick}
            />

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between border-t pt-4">
                <p className="text-sm text-muted-foreground">
                  Page {currentPage} of {totalPages} ({pagination.totalItems} suppliers)
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      handlePageChange(currentPage - 1);
                    }}
                    disabled={currentPage <= 1 || overviewFetching}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      handlePageChange(currentPage + 1);
                    }}
                    disabled={currentPage >= totalPages || overviewFetching}
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
